import { Globe, Instagram, Facebook, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { ShopAvatar } from "./ShopAvatar";
import { getCountryFlagEmoji, getCountryName } from "@/lib/community/utils";
import type { PublicCommunityShop } from "@/lib/community/types";


interface ShopProfileHeaderProps {
  shop: PublicCommunityShop;
  totalProducts: number;
  totalPartners?: number;
  featuredCollections?: { internal_id: string; name: string }[];
}

const linkConfig = [
  { key: "website_url" as const, label: "Website", icon: Globe },
  { key: "instagram_url" as const, label: "Instagram", icon: Instagram },
  { key: "facebook_url" as const, label: "Facebook", icon: Facebook },
  { key: "other_url" as const, label: "Other", icon: ExternalLink },
];

const RETRO_BADGE_CLASS =
  "rounded-md border border-retro-line bg-retro-paper font-mono text-xs font-bold uppercase tracking-[0.08em] text-brand-green";

export function ShopProfileHeader({
  shop,
  totalProducts,
  totalPartners,
  featuredCollections,
}: ShopProfileHeaderProps) {
  return (
    <Section
      size="sm"
      background="gradient"
      className="pt-6 pb-2 md:pt-8 md:pb-3 lg:pt-10 lg:pb-4"
    >
      <Container size="md">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <ShopAvatar shop={shop} size="xl" variant="store" />
            <h1 className="text-display-2 text-ink truncate whitespace-nowrap max-w-[min(600px,calc(100vw-120px))]">
              {shop.name ?? "Unnamed shop"}
            </h1>
          </div>

          <div className="flex flex-col items-center gap-2 mt-3">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {shop.main_category && (
                <Badge className={RETRO_BADGE_CLASS}>
                  {shop.main_category.name}
                </Badge>
              )}
              {(featuredCollections ?? []).map((collection) => (
                <Badge
                  key={collection.internal_id}
                  className={RETRO_BADGE_CLASS}
                >
                  {collection.name}
                </Badge>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {shop.country_code && (
                <Badge variant="default" className="truncate max-w-[200px]">
                  {getCountryFlagEmoji(shop.country_code)}{" "}
                  {getCountryName(shop.country_code) ?? shop.country_code}
                </Badge>
              )}
              {totalProducts > 0 && (
                <Badge variant="default">{totalProducts} products</Badge>
              )}
              {typeof totalPartners === "number" && totalPartners > 0 && (
                <Badge variant="default">{totalPartners} partners</Badge>
              )}
            </div>
          </div>

          {shop.description && (
            <p className="mt-4 text-muted line-clamp-4 max-w-2xl mx-auto">
              {shop.description}
            </p>
          )}

          <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
            {linkConfig.map(({ key, label, icon: Icon }) => {
              const url = shop.links?.[key];
              if (!url) return null;
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-brand-green transition-colors"
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </a>
              );
            })}
          </div>
        </div>
      </Container>
    </Section>
  );
}
